import React from "react";

/**
 * Canonical cockpit primitives — one source of truth for the institutional
 * dashboard look (chip / label / card / hint / stat tile / money). Pages should
 * import these instead of re-defining their own so the look can never drift.
 *
 * Theme-safe: uses the global CSS variables (theme.css). Note --card-border ===
 * --border-color and --text-color === --text-primary (identical values), so this
 * matches the Risk, Signals, and Fixed Income pages alike.
 */

/**
 * Tenor-bucket display label. The wire values are engine keys (`30m_plus`), and a raw key
 * on screen reads like a leaked identifier. Shared so the Risk and Signals pages cannot
 * print the same bucket two different ways.
 *
 * Deliberately falls through to the raw key rather than prettifying an unknown bucket: a
 * new engine bucket should look unfamiliar, not be silently dressed up as a known one.
 */
export function bucketLabel(bucket?: string | null): string {
  if (!bucket) return "—";
  const map: Record<string, string> = {
    "18_24m": "18–24m",
    "24_30m": "24–30m",
    "30m_plus": "30m+",
    under_18m: "<18m",
  };
  return map[bucket] || bucket;
}

// ---- Chips -----------------------------------------------------------------

// Tones follow the shared semantic system: positive=good, warning=amber caution,
// elevated=orange (the 80–94th stress band), danger=red, info=sky, neutral=slate.
// "elevated" sits between warning and danger so the stress ramp has four distinct
// steps (see cockpit/stress.ts). Gold-as-"mid" is intentionally absent.
export type ChipTone = "positive" | "warning" | "elevated" | "danger" | "info" | "neutral";

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  positive: "bg-up/15 text-up ring-1 ring-up/30",
  warning: "bg-warn/15 text-warn ring-1 ring-warn/30",
  elevated: "bg-elevated/15 text-elevated ring-1 ring-elevated/30",
  danger: "bg-down/15 text-down ring-1 ring-down/30",
  info: "bg-info/15 text-info ring-1 ring-info/30",
  neutral: "bg-subtle/15 text-subtle ring-1 ring-subtle/30",
};

/** Subtle tint+ring chip classes for a tone — use when you need a className string. */
export const chipClass = (tone: ChipTone = "neutral", extra = ""): string =>
  `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${CHIP_TONE_CLASS[tone]} ${extra}`.trim();

export function Chip({
  tone = "neutral",
  title,
  className = "",
  children,
}: {
  tone?: ChipTone;
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span className={chipClass(tone, className)} title={title}>
      {children}
    </span>
  );
}

// ---- Labels & glossary hint ------------------------------------------------

/** Uppercase micro-label used as the title on every stat tile. */
export const MICRO_LABEL = "text-[10px] uppercase tracking-[0.16em] font-semibold opacity-70";

/** Inline "i" affordance with a native tooltip explaining a term in place. */
export const InfoHint: React.FC<{ text: string }> = ({ text }) => (
  <span
    title={text}
    aria-label={text}
    className="ml-1 inline-flex h-[14px] w-[14px] cursor-help items-center justify-center rounded-full border text-[9px] opacity-60 align-middle"
    style={{ borderColor: "var(--card-border)" }}
  >
    i
  </span>
);

export const TileLabel: React.FC<{
  hint?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ hint, className = "", children }) => (
  <p className={`${MICRO_LABEL} ${className}`.trim()} style={{ color: "var(--text-muted)" }}>
    {children}
    {hint ? <InfoHint text={hint} /> : null}
  </p>
);

// ---- Cards -----------------------------------------------------------------

export const CARD_CLASS = "p-3.5 rounded-xl";

export const cardStyle: React.CSSProperties = {
  backgroundColor: "var(--card-bg)",
  border: "1px solid var(--card-border)",
  color: "var(--text-primary)",
  boxShadow: "var(--shadow-soft)",
};

export const Card: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ className = "", style, children }) => (
  <div className={`${CARD_CLASS} ${className}`.trim()} style={{ ...cardStyle, ...style }}>
    {children}
  </div>
);

/** Label + big value + optional subtext — the workhorse stat tile. */
export const StatTile: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  subtext?: React.ReactNode;
  hint?: string;
  valueClassName?: string;
  className?: string;
}> = ({ label, value, subtext, hint, valueClassName = "", className }) => (
  <Card className={className}>
    <TileLabel hint={hint}>{label}</TileLabel>
    {/* The inline colour is the DEFAULT, not an override. It used to be set
        unconditionally, which silently beat every `valueClassName` a caller passed --
        so "text-warn" and "text-down" have never actually rendered on any tile in the
        app, and a stat that was supposed to shout about a break looked identical to a
        healthy one. Callers that ask for a colour now get it. */}
    <div
      className={`text-lg font-semibold mt-1 leading-none ${valueClassName}`.trim()}
      style={valueClassName ? undefined : { color: "var(--text-primary)" }}
    >
      {value}
    </div>
    {subtext ? (
      <div className="text-[11px] mt-1 leading-5" style={{ color: "var(--text-muted)" }}>
        {subtext}
      </div>
    ) : null}
  </Card>
);

/** A titled section container with an optional subtitle and right-aligned slot. */
export const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}> = ({ title, subtitle, right, className = "", children }) => (
  <div className={`p-3.5 rounded-xl ${className}`.trim()} style={cardStyle}>
    <div className="flex flex-col gap-2 mb-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="space-y-1">
        <div className="font-semibold text-[15px] leading-none">{title}</div>
        {subtitle ? (
          <div className="text-[11px] leading-5" style={{ color: "var(--text-muted)" }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
    {children}
  </div>
);

// ---- Money -----------------------------------------------------------------

/** Whole-dollar formatter (no cents) — fund scale. */
export const fmtUSD = (n?: number | null): string =>
  n == null || Number.isNaN(n)
    ? "—"
    : `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
